import { useIntl } from 'react-intl';
import { MUSIC24_TRIAL_LEVEL_TIER } from 'fym-shared/src/music24/music24Plans';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { useMusic24Plan } from './useMusic24Plan';
import { planNameMessages } from '../config/music24PlanNames';
import { trialStatusMessages } from '../trialStatus/trialStatusMessages';

export type PlanStatusVariant = 'free' | 'trial' | 'paid' | 'lapsed';

export interface PlanStatus {
  /** design s3: the colour of the status dot next to the plan label. */
  variant: PlanStatusVariant;
  label: string;
}

/**
 * The plan label of the sidebar user chip and the mobile account sheet (design s3, b5).
 *
 * `isSubscriptionValid()` only tracks a paid subscription's expiry — a Free or trial user has no
 * `subscriptionExpiresAt` at all, so it always reads false for them. The Music24 plan itself (the
 * same hook the trial pill uses) gives Free and trial users their own wording instead of the
 * paid-only "No active subscription" text.
 */
export function usePlanStatus(): PlanStatus {
  const intl = useIntl();
  const licenseInfo = useLicenseInfo();
  const plan = useMusic24Plan();
  const isTrialRunning = plan === 'trial' && licenseInfo.isTrialActive;

  if (isTrialRunning) {
    // design: onboarding h3. The days show once, in the trial pill; this line names the plan only.
    return {
      variant: 'trial',
      label: intl.formatMessage(trialStatusMessages.switcherTrial, {
        plan: intl.formatMessage(planNameMessages[MUSIC24_TRIAL_LEVEL_TIER]),
      }),
    };
  }
  if (plan === 'free') {
    return {
      variant: 'free',
      label: intl.formatMessage({ id: 'app.sml.sidebar.user.free', defaultMessage: 'Free plan' }),
    };
  }
  if (licenseInfo.isSubscriptionValid()) {
    return {
      variant: 'paid',
      // design: navigation-v2 1a. The account card names the plan: "Pro plan".
      label: intl.formatMessage(
        { id: 'app.sml.sidebar.user.paidPlan', defaultMessage: '{plan} plan' },
        { plan: intl.formatMessage(planNameMessages[plan]) }
      ),
    };
  }
  return {
    variant: 'lapsed',
    label: intl.formatMessage({ id: 'app.sml.sidebar.user.inactive', defaultMessage: 'No active subscription' }),
  };
}
